import { solveShot } from '../aim/solver.js';
import type { Difficulty, Entity, GameState, Intent } from '../types.js';
import { WEAPONS, WEAPON_IDS } from '../weapons/catalog.js';

/** Angle noise (degrees) per difficulty — also drives power jitter. */
const SIGMA: Record<Difficulty, number> = { easy: 8, normal: 3, hard: 0.8 };

/**
 * Basic CPU brain (phase 3): pick the nearest enemy, the best affordable
 * fruit, solve a shot with the real integrator, sprinkle difficulty noise.
 * The full pipeline (move sampling, friendly-fire scoring, tactical
 * swap) lands in phase 7 — this keeps the same call signature.
 *
 * Deterministic: all randomness flows through `state.rng`.
 */
export function decideAI(state: GameState, active: Entity): Intent {
  const pass = (): Intent => ({
    playerId: '',
    turnIndex: state.turnIndex,
    kind: 'pass',
  });

  const enemies = state.entities.filter((e) => e.alive && e.teamId !== active.teamId);
  if (enemies.length === 0) return pass();

  const team = state.teams.find((t) => t.id === active.teamId);
  if (!team) return pass();

  // Nearest enemy (feet distance).
  let target = enemies[0];
  let bd = Infinity;
  for (const e of enemies) {
    const d = Math.hypot(e.x - active.x, e.y - active.y);
    if (d < bd) {
      bd = d;
      target = e;
    }
  }

  // Best affordable fruit: highest damage with ammo, with a light range tilt.
  const affordable = WEAPON_IDS.filter((id) => team.ammo[id] > 0);
  if (affordable.length === 0) return pass();
  let weaponId = affordable[0];
  let bestScore = -Infinity;
  for (const id of affordable) {
    const def = WEAPONS[id];
    // Prefer bombs up close, faster shells at range.
    const rangeFit = bd < 260 ? def.radius : def.speed / 8;
    const score = def.maxDmg * 0.6 + rangeFit;
    if (score > bestScore) {
      bestScore = score;
      weaponId = id;
    }
  }

  const def = WEAPONS[weaponId];
  const from = { x: active.x, y: active.y - 24 };
  const to = { x: target.x, y: target.y - 16 };
  const minSpeed = def.speed * 0.35;
  const maxSpeed = def.speed;
  const shot = solveShot(from, to, state.terrain, state.wind, {
    minSpeed,
    maxSpeed,
    gravityScale: def.gravityScale,
    windScale: def.windScale,
  });
  if (!shot) return pass();

  // Difficulty: aim error + power jitter, both from the sim rng.
  const sigma = SIGMA[state.config.difficulty];
  const angleDeg = (shot.angle * 180) / Math.PI + state.rng.normal() * sigma;
  const speed =
    shot.speed * (1 + (state.rng.normal() * sigma) / 140);
  const clampedSpeed = Math.min(maxSpeed, Math.max(minSpeed, speed));
  const a = (angleDeg * Math.PI) / 180;
  const vx = Math.cos(a) * clampedSpeed;
  const vy = -Math.sin(a) * clampedSpeed;

  // Engine derives launch angle from (target - origin) and speed from power.
  const power = Math.min(1, Math.max(0.1, (clampedSpeed - minSpeed) / (maxSpeed - minSpeed)));
  return {
    playerId: '',
    turnIndex: state.turnIndex,
    kind: 'fire',
    weaponId,
    target: { x: from.x + vx, y: from.y + vy },
    power,
  };
}
